import { client } from "../../../../api/client";

const parseObjectField = (value) => {
  if (value === null || value === undefined || value === "") return {};

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  return {};
};

const parseArrayField = (value) => {
  if (value === null || value === undefined || value === "") return [];

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [];
};

const normalizeTemplateFromApi = (item) => {
  if (!item || typeof item !== "object") return item;

  return {
    ...item,
    customFields: parseArrayField(item.customFields),
    customSections: parseArrayField(item.customSections),
    customFieldsLayout: parseArrayField(item.customFieldsLayout),
    previewSectionValues: parseObjectField(item.previewSectionValues),
  };
};

const normalizeTemplateResponse = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeTemplateFromApi(item));
  }

  if (data && typeof data === "object" && Array.isArray(data.documentTemplateItems)) {
    return {
      ...data,
      documentTemplateItems: data.documentTemplateItems.map((item) => normalizeTemplateFromApi(item)),
    };
  }

  return normalizeTemplateFromApi(data);
};

const stringifyPreviewSectionValues = (value) => JSON.stringify(parseObjectField(value));

const stringifyIfObject = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

const normalizeCreateBody = (body) => {
  if (body instanceof FormData) {
    const normalized = new FormData();
    for (const [key, value] of body.entries()) {
      if (key !== "previewSectionValues") {
        normalized.append(key, value);
      }
    }
    normalized.set("previewSectionValues", stringifyPreviewSectionValues(body.get("previewSectionValues")));
    return normalized;
  }

  if (body && typeof body === "object") {
    const normalized = new FormData();

    if (body.file) normalized.set("file", body.file);
    if (body.name !== undefined) normalized.set("name", body.name);
    if (body.description !== undefined) normalized.set("description", body.description);
    if (body.disabled !== undefined) normalized.set("disabled", body.disabled);

    if (body.customFields !== undefined) {
      normalized.set("customFields", stringifyIfObject(body.customFields));
    }

    if (body.customSections !== undefined) {
      normalized.set("customSections", stringifyIfObject(body.customSections));
    }

    if (body.customFieldsLayout !== undefined) {
      normalized.set("customFieldsLayout", stringifyIfObject(body.customFieldsLayout));
    }

    normalized.set("previewSectionValues", stringifyPreviewSectionValues(body.previewSectionValues));
    return normalized;
  }

  return body;
};

const normalizeUpdateBody = (body) => {
  if (!body || typeof body !== "object") return body;

  return {
    ...body,
    customFields: stringifyIfObject(body.customFields),
    customSections: stringifyIfObject(body.customSections),
    customFieldsLayout: stringifyIfObject(body.customFieldsLayout),
    previewSectionValues: stringifyPreviewSectionValues(body.previewSectionValues),
  };
};

export const fetchDocumentTemplate = async () => {
  if (process.env.REACT_APP_USELOCALDATA === "true") {
    return normalizeTemplateResponse(require("./_local/_documentTemplateData.json"));
  } else {
    try {
      const response = await client.get(process.env.REACT_APP_GATEWAY_URI, "templatesapi/documenttemplate");
      return normalizeTemplateResponse(response.data);
    } catch (error) {
      console.error("Error fetching document template:", error);
      return [];
    }
  }
}

export const fetchDocumentTemplateByFilter = async (body) => {
    if (process.env.REACT_APP_USELOCALDATA === "true") { 
        return normalizeTemplateResponse(require("./_local/_documentTemplateData.json"));
    } else {
        try {
          const response = await client.post(process.env.REACT_APP_GATEWAY_URI,"templatesapi/documenttemplate/filter", body);
          return normalizeTemplateResponse(response.data);
        } catch (error) {
            console.error("Error fetching document template:", error);
            return [];
        }
      }
}

export const deleteDocumentTemplate = async (id) => {
    if (process.env.REACT_APP_USELOCALDATA === "false") {
        try {
            const response = await client.delete(process.env.REACT_APP_GATEWAY_URI, `templatesapi/documenttemplate/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting document template:", error);
            return [];
        }
    } else {
        return { "data": { newId: Math.floor(Math.random() * 100000) } };
    }
};

export const createDocumentTemplate = async (body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
        const response = await client.post(process.env.REACT_APP_GATEWAY_URI,"templatesapi/documenttemplate", normalizeCreateBody(body));
        return normalizeTemplateResponse(response.data);
    } catch (error) {
        console.error("Error creating document template:", error);
        return [];
    }
  } else {
    return { "data": { newId: Math.floor(Math.random() * 100000) } };
  }
};


export const updateDocumentTemplate = async (id, body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
        const response = await client.put(process.env.REACT_APP_GATEWAY_URI,`templatesapi/documenttemplate/${id}`, normalizeUpdateBody(body));
        return normalizeTemplateResponse(response.data);
    } catch (error) {
        console.error("Error updating document template:", error);
        return [];
    }
  }
};

export const replaceDocumentTemplateFile = async (id, body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.put(process.env.REACT_APP_GATEWAY_URI, `templatesapi/documenttemplate/replacefile/${id}`, body);
      return response.data;
    } catch (error) {
      console.error("Error replacing document template file:", error);
      return null;
    }
  } else {
    // mimic the backend echoing back the key it actually stored the file under
    const file = body?.get?.("file");
    return { s3Key: file ? `documenttemplates/${file.name}` : null };
  }
};

export const getAWSSettings = async () => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.get(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/documenttemplate/getawssettings');
      return response.data;
    } catch (error) {
        console.error("Error getting AWS Settings:", error);
        return [];
    }
  }
};

export const getPreSignedUrl = async (key) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.post(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/documenttemplate/presignedurl/key', key);
      return response.data;
    } catch (error) {
        console.error("Error getting presigned url:", error);
        return [];
    }
  }
};
